import { Step } from '../../types/tutor';
import { cn } from '../../lib/utils';
import { CheckCircle2 } from 'lucide-react';

interface StepOutlineProps {
  steps: Step[];
  currentStepId: string;
  onStepClick: (stepId: string) => void;
  className?: string;
}

export function StepOutline({ steps, currentStepId, onStepClick, className }: StepOutlineProps) {
  return (
    <div className={cn("flex flex-col h-full bg-slate-50 border-r border-slate-200", className)}>
      <div className="p-4 border-b border-slate-200 bg-white">
        <h2 className="font-semibold text-slate-800">解题步骤</h2>
        <p className="text-xs text-slate-500 mt-1">共 {steps.length} 步</p>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {steps.map((step, index) => {
          const isActive = step.step_id === currentStepId;
          const isLocked = step.is_locked;
          const isCompleted = step.is_done && !isActive;

          return (
            <button
              key={step.step_id}
              onClick={() => onStepClick(step.step_id)}
              className={cn(
                "w-full text-left p-3 rounded-lg flex items-start gap-3 transition-all duration-200",
                isActive 
                  ? "bg-blue-50 border-blue-200 shadow-sm ring-1 ring-blue-200" 
                  : "hover:bg-slate-100 border border-transparent",
                isLocked && "opacity-60"
              )}
            >
              <div className="mt-0.5 shrink-0">
                {isCompleted ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                ) : (
                  <div className={cn(
                    "w-5 h-5 rounded-full border-2 flex items-center justify-center text-[10px] font-bold",
                    isActive ? "border-blue-500 text-blue-600 bg-white" : "border-slate-300 text-slate-400"
                  )}>
                    {index + 1}
                  </div>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className={cn(
                  "text-sm font-medium truncate",
                  isActive ? "text-blue-700" : "text-slate-700"
                )}>
                  {step.title}
                </div>
                {step.highlights && step.highlights.length > 0 && (
                  <div className="flex gap-1 mt-1.5 flex-wrap">
                    {step.highlights.map((h, i) => (
                      <span 
                        key={i}
                        className={cn(
                          "text-[10px] px-1.5 py-0.5 rounded-full border",
                          h.type === 'key' && "bg-amber-50 text-amber-600 border-amber-200",
                          h.type === 'pitfall' && "bg-rose-50 text-rose-600 border-rose-200",
                          h.type === 'result' && "bg-emerald-50 text-emerald-600 border-emerald-200"
                        )}
                      >
                        {h.text}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
